'use client'

import { CompanyData } from '@/types/company'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

interface CompanyDataContextType {
    companyData: CompanyData
    updateCompanyData: (data: Partial<CompanyData>) => void
}

const defaultCompanyData: CompanyData = {
    name: '',
    industry: '',
    customIndustry: '',
    companyType: '',
    companySize: '',
    internationalFocus: '',
    communicationLanguages: [],
    customLanguage: '',
    website: '',
    locations: [],
    valuesDescription: '',
    description: '',
    dealbreakers: '',
    benefitsNote: '',
    benefits: [],
    offerings: [],
    testimonials: [],
    contactPersons: [],
    cultureStructure: 50,
    cultureIndependence: 50,
    cultureSocialCloseness: 50,
    cultureStability: 50,
    culturePracticeIntegration: 50,
    cultureIndividualization: 50,
    cultureRotation: 50,
    cultureValueOrientation: 50,
}

const CompanyDataContext = createContext<CompanyDataContextType | undefined>(undefined)

export const CompanyDataProvider = ({ children }: { children: ReactNode }) => {
    const [companyData, setCompanyData] = useState<CompanyData>(defaultCompanyData)

    useEffect(() => {
        const stored = localStorage.getItem('wcData')
        if (stored) {
            try {
                const setData = () => setCompanyData(JSON.parse(stored))
                setData()
            } catch {
                // ignore corrupted data
            }
        }
    }, [])

    const updateCompanyData = (data: Partial<CompanyData>) => {
        setCompanyData((prev) => {
            const updated = { ...prev, ...data }
            localStorage.setItem('wcData', JSON.stringify(updated))
            return updated
        })
    }

    return (
        <CompanyDataContext.Provider value={{ companyData, updateCompanyData }}>{children}</CompanyDataContext.Provider>
    )
}

export const useCompanyData = () => {
    const context = useContext(CompanyDataContext)
    if (context === undefined) {
        throw new Error('useCompanyData must be used within a CompanyDataProvider')
    }
    return context
}
